import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';

const PageManager = ({ pages, activePage, onSelectPage, onAddPage, onDeletePage, onRenamePage }) => {
  const [isAddingPage, setIsAddingPage] = useState(false);
  const [newPageName, setNewPageName] = useState('');
  const [editingPageId, setEditingPageId] = useState(null);
  const [editPageName, setEditPageName] = useState('');

  const handleAddPage = (e) => {
    if (e) {
      e?.preventDefault();
      e?.stopPropagation();
    }
    if (newPageName?.trim()) {
      onAddPage(newPageName?.trim());
      setNewPageName('');
      setIsAddingPage(false);
    }
  };

  const handleRename = (e, pageId) => {
    if (e) {
      e?.preventDefault();
      e?.stopPropagation();
    }
    if (editPageName?.trim()) {
      onRenamePage(pageId, editPageName?.trim());
      setEditingPageId(null);
      setEditPageName('');
    }
  };

  const startEditing = (e, page) => {
    if (e) {
      e?.preventDefault();
      e?.stopPropagation();
    }
    setEditingPageId(page?.id);
    setEditPageName(page?.name);
  };

  const handleKeyPress = (e, action, pageId) => {
    if (e?.key === 'Enter') {
      e?.preventDefault();
      e?.stopPropagation();
      if (action === 'add') {
        handleAddPage(e);
      } else if (action === 'rename') {
        handleRename(e, pageId);
      }
    }
  };

  return (
    <div className="px-4 py-3">
      <div className="flex items-center gap-3">
        {/* Page Tabs */}
        <div className="flex items-center gap-2 flex-1 overflow-x-auto">
          {pages?.map((page) => {
            const isActive = activePage === page?.id;
            const isEditing = editingPageId === page?.id;

            return (
              <div key={page?.id} className="group flex-shrink-0">
                {isEditing ? (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg">
                    <input
                      type="text"
                      value={editPageName}
                      onChange={(e) => setEditPageName(e?.target?.value)}
                      onKeyPress={(e) => handleKeyPress(e, 'rename', page?.id)}
                      onBlur={(e) => handleRename(e, page?.id)}
                      autoFocus
                      className="w-24 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                ) : (
                  <div
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${
                      isActive
                        ? 'bg-blue-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={(e) => {
                        e?.preventDefault();
                        e?.stopPropagation();
                        onSelectPage(page?.id);
                      }}
                      className="flex items-center gap-2 text-sm font-medium"
                    >
                      <Icon 
                        name={page?.icon || 'FileText'} 
                        size={16} 
                        className={isActive ? 'text-white' : 'text-gray-600'}
                      />
                      <span>{page?.name}</span>
                    </button>
                    
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-2">
                      <button
                        type="button"
                        onClick={(e) => startEditing(e, page)}
                        className="p-0.5 hover:bg-white/20 rounded transition-colors"
                        aria-label="Rename page"
                      >
                        <Icon name="Edit2" size={12} className={isActive ? 'text-white' : 'text-gray-600'} />
                      </button>
                      {pages?.length > 1 && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e?.preventDefault();
                            e?.stopPropagation();
                            onDeletePage(page?.id);
                          }}
                          className="p-0.5 hover:bg-red-500/20 rounded transition-colors"
                          aria-label="Delete page"
                        >
                          <Icon name="Trash2" size={12} className="text-red-500" />
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Add Page Button/Input */}
          {isAddingPage ? (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg flex-shrink-0">
              <input
                type="text"
                placeholder="Page name"
                value={newPageName}
                onChange={(e) => setNewPageName(e?.target?.value)}
                onKeyPress={(e) => handleKeyPress(e, 'add')}
                onBlur={(e) => {
                  if (!newPageName?.trim()) {
                    setIsAddingPage(false);
                  }
                }}
                autoFocus
                className="w-24 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={(e) => handleAddPage(e)}
                className="p-0.5 hover:bg-green-500/20 rounded transition-colors"
                aria-label="Confirm add page"
              >
                <Icon name="Check" size={12} className="text-green-600" />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e?.preventDefault();
                  e?.stopPropagation();
                  setIsAddingPage(false);
                  setNewPageName('');
                }}
                className="p-0.5 hover:bg-red-500/20 rounded transition-colors"
                aria-label="Cancel add page"
              >
                <Icon name="X" size={12} className="text-red-500" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={(e) => {
                e?.preventDefault();
                e?.stopPropagation();
                setIsAddingPage(true);
              }}
              className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex-shrink-0"
              aria-label="Add new page"
            >
              <Icon name="Plus" size={14} />
              <span className="text-xs font-medium">Add</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PageManager;